import React, { useMemo } from 'react';
import { useIntl } from 'react-intl';
import { StreamingOverviewResponse } from 'fym-shared/src/music24/streaming';
import { TypedMemo } from '../../../common/types';
import { ArtistPortalRangeDays } from '../../../../redux/api/fymAPI.types';
import { useStreamingTracks } from '../hooks/useStreamingTracks';
import { streamingSectionPath } from '../streamingPaths';
import { streamingBlockMessages as messages } from '../streamingBlockMessages';
import { StreamingBlock } from '../parts/StreamingBlock';
import { TrackHead } from '../parts/TrackHead';
import { TrackSourceChart } from '../parts/TrackSourceChart';
import { TrackSourcesTable } from '../parts/TrackSourcesTable';
import { DimensionBlock } from '../blocks/DimensionBlock';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for the whole screen; each part uses its own slice of it */
import styles from '../Streaming.module.scss';
/* eslint-enable css-modules/no-unused-class */

interface TrackDetailSectionProps {
  accountId: number;
  overview: StreamingOverviewResponse;
  /** `StreamingTrackRow.key` from the address. */
  trackKey: string;
  onNavigate: (path: string) => void;
}

/**
 * One track (design d2): the head and the totals, the streams per day of each source, the stores
 * and the countries of the distributor, and what each source reports.
 */
const TrackDetailSectionComponent: React.FC<TrackDetailSectionProps> = ({
  accountId,
  overview,
  trackKey,
  onNavigate,
}) => {
  const intl = useIntl();
  const { data, isLoading, error, retry } = useStreamingTracks(accountId, overview.days);
  const track = useMemo(() => data?.tracks.find((one) => one.key === trackKey), [data, trackKey]);
  const days = overview.days as ArtistPortalRangeDays;

  return (
    <StreamingBlock
      title={track?.name ?? intl.formatMessage(messages.backToTracks)}
      moreLabel={intl.formatMessage(messages.backToTracks)}
      onMore={() => onNavigate(streamingSectionPath('tracks', overview.days))}
      isLoading={isLoading}
      error={error}
      retry={retry}
      testId="streaming-section-track"
    >
      {track === undefined ? (
        <p className={styles.faint}>{intl.formatMessage(messages.trackNotFound)}</p>
      ) : (
        <>
          <TrackHead track={track} />
          {track.bySource.map((one) => (
            <TrackSourceChart
              key={one.integrationType}
              accountId={accountId}
              integrationType={one.integrationType}
              externalId={one.externalId}
              days={days}
            />
          ))}
          {track.distributorTrackId !== null ? (
            <div className={styles.blocksTwo}>
              <DimensionBlock
                accountId={accountId}
                dimension="store"
                days={overview.days}
                trackExternalId={track.distributorTrackId}
                title={intl.formatMessage(messages.byStore)}
                onNavigate={onNavigate}
                nested
              />
              <DimensionBlock
                accountId={accountId}
                dimension="country"
                days={overview.days}
                trackExternalId={track.distributorTrackId}
                title={intl.formatMessage(messages.byCountry)}
                onNavigate={onNavigate}
                nested
              />
            </div>
          ) : null}
          <h3 className={styles.blockTitle}>{intl.formatMessage(messages.whatEachReports)}</h3>
          <TrackSourcesTable bySource={track.bySource} />
        </>
      )}
    </StreamingBlock>
  );
};

export const TrackDetailSection = TypedMemo(TrackDetailSectionComponent);
